import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import '../styles/BarcodeScanner.css';

const BarcodeScanner = ({ isOpen, onClose, onScan, title = 'Scan Barcode' }) => {
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      startScanner();
    }

    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    setError('');
    setIsScanning(true);

    try {
      html5QrCodeRef.current = new Html5Qrcode('barcode-reader');

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 150 },
        aspectRatio: 1.0,
        formatsToSupport: [
          0,  // QR_CODE
          1,  // AZTEC
          2,  // CODABAR
          3,  // CODE_39
          4,  // CODE_93
          5,  // CODE_128
          6,  // DATA_MATRIX
          7,  // MAXICODE
          8,  // ITF
          9,  // EAN_13
          10, // EAN_8
          11, // PDF_417
          12, // RSS_14
          13, // RSS_EXPANDED
          14, // UPC_A
          15, // UPC_E
          16, // UPC_EAN_EXTENSION
        ]
      };

      await html5QrCodeRef.current.start(
        { facingMode: 'environment' }, // Use back camera
        config,
        (decodedText, decodedResult) => {
          // Successfully scanned
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Scan error - ignore, keep scanning
        }
      );
    } catch (err) {
      setIsScanning(false);
      if (err.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access to scan barcodes.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Failed to start camera. Please try again.');
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore cleanup errors
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const handleScanSuccess = async (decodedText) => {
    // Stop scanner before calling callback
    await stopScanner();

    // Call the onScan callback with the scanned value
    if (onScan) {
      onScan(decodedText);
    }

    // Close the modal
    onClose();
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="scanner-overlay" onClick={handleClose}>
      <div className="scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="scanner-header">
          <h2>{title}</h2>
          <button className="scanner-close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="scanner-body">
          {error ? (
            <div className="scanner-error">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button className="retry-btn" onClick={startScanner}>
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div id="barcode-reader" ref={scannerRef}></div>
              {isScanning && (
                <div className="scanner-instructions">
                  <p>Point camera at barcode</p>
                  <div className="scanner-hint">
                    Supports: Code 128, EAN-13, UPC-A, QR Code, and more
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="scanner-footer">
          <button className="cancel-btn" onClick={handleClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
